import { useTrail, animated } from "@react-spring/web";
import { GitBranch, Plus } from "lucide-react";
import { useGitBranches, useDefaultGitBranch } from "@/hooks/git";
import type { GitBranch as GitBranchType } from "@/services/git/types";
import { ListSkeleton, EmptyList } from "./shared";

// ─── Branch List ──────────────────────────────────────────────────────────────

export function BranchList({
  repoFullName,
  defaultBranch,
  selectedName,
  onSelect,
  onCreate,
}: {
  repoFullName: string;
  defaultBranch: string;
  selectedName: string | null;
  onSelect: (branch: GitBranchType) => void;
  onCreate: () => void;
}) {
  const { data: branches = [], isLoading } = useGitBranches(repoFullName);
  const { data: defaultBranchData } = useDefaultGitBranch();
  const workingBranchId = defaultBranchData?.id ?? null;

  if (isLoading) return <ListSkeleton />;
  if (branches.length === 0) return <EmptyList text="No branches found." />;

  return (
    <BranchItems
      branches={branches}
      defaultBranch={defaultBranch}
      workingBranchId={workingBranchId}
      selectedName={selectedName}
      onSelect={onSelect}
      onCreate={onCreate}
    />
  );
}

function BranchItems({
  branches, defaultBranch, workingBranchId, selectedName, onSelect, onCreate,
}: {
  branches: GitBranchType[];
  defaultBranch: string;
  workingBranchId: string | null;
  selectedName: string | null;
  onSelect: (branch: GitBranchType) => void;
  onCreate: () => void;
}) {
  const trail = useTrail(branches.length, {
    from: { opacity: 0, y: 4 },
    to: { opacity: 1, y: 0 },
    config: { tension: 280, friction: 34, clamp: true },
  });

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between border-b border-border/50 px-3 py-1.5">
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {branches.length} branch{branches.length !== 1 ? "es" : ""}
        </span>
        <button
          type="button"
          onClick={onCreate}
          className="flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
        >
          <Plus className="h-3 w-3" />
          New
        </button>
      </div>

      {trail.map((style, i) => {
        const b = branches[i];
        const isDefault = b.name === defaultBranch;
        const isWorking = b.id !== null && b.id === workingBranchId;
        const isSelected = b.name === selectedName;

        return (
          <animated.button
            key={b.name}
            style={style}
            type="button"
            onClick={() => onSelect(b)}
            className={[
              "w-full border-b border-border/50 px-3 py-2.5 text-left transition-colors last:border-0",
              isSelected ? "bg-primary/10" : "hover:bg-muted/30",
            ].join(" ")}
          >
            <div className="flex min-w-0 items-center gap-2">
              <GitBranch className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="truncate font-mono text-xs font-medium">{b.name}</span>
              {isDefault && (
                <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold text-primary">
                  default
                </span>
              )}
              {isWorking && (
                <span className="shrink-0 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold text-emerald-400">
                  working
                </span>
              )}
            </div>
            <p className="mt-0.5 font-mono text-[10px] text-muted-foreground">{b.sha.slice(0, 7)}</p>
          </animated.button>
        );
      })}
    </div>
  );
}
